import type { FabricObject } from "fabric";
import {
  ASSET_LAYER_TYPE,
  CIRCLE_SHAPE_TYPE,
  IMAGE_LAYER_TYPE,
  LINE_LAYER_TYPE,
  RECT_SHAPE_TYPE,
  SHAPE_LAYER_TYPE,
  TEXT_LAYER_TYPE,
  TRIANGLE_SHAPE_TYPE,
} from "./constants.ts";
import type {
  AssetLayer,
  ImageLayer,
  Layer,
  LineLayer,
  ShapeLayer,
  TextLayer,
} from "./types.ts";

// XXX: Those linter exceptions are essential for runtime type safety, even if TypeScript thinks these checks are unnecessary based on static type.
// Indeed, the layers are built on top of Fabric.js objects that might be in intermediate states during creation, deserialization,
// or when converted from external sources where these properties might not be initialized yet. The `!!` checks provide runtime validation to ensure
// these properties actually exist and are truthy, which is more defensive than relying solely on TypeScript's compile-time type checking.

export const isTextLayer = (layer: Layer): layer is TextLayer =>
  layer.layerType === TEXT_LAYER_TYPE &&
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  !!layer.shadowStyle &&
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  !!layer.shadowDirection &&
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  !!layer.shadowDistance &&
  !!layer.strokeStyle;

export const isImageLayer = (layer: Layer): layer is ImageLayer =>
  layer.layerType === IMAGE_LAYER_TYPE && !!layer.contentUuid;

export const isAssetLayer = (layer: Layer): layer is AssetLayer =>
  layer.layerType === ASSET_LAYER_TYPE && !!layer.assetUuid;

export const isLineLayer = (layer: Layer): layer is LineLayer =>
  layer.layerType === LINE_LAYER_TYPE && !!layer.strokeStyle;

export const isSquareShapeLayer = (layer: Layer): layer is ShapeLayer =>
  layer.layerType === SHAPE_LAYER_TYPE && layer.shapeType === RECT_SHAPE_TYPE;

export const isCircleShapeLayer = (layer: Layer): layer is ShapeLayer =>
  layer.layerType === SHAPE_LAYER_TYPE && layer.shapeType === CIRCLE_SHAPE_TYPE;

export const isTriangleShapeLayer = (layer: Layer): layer is ShapeLayer =>
  layer.layerType === SHAPE_LAYER_TYPE &&
  layer.shapeType === TRIANGLE_SHAPE_TYPE;

export const isShapeLayer = (layer: Layer): layer is ShapeLayer =>
  (isSquareShapeLayer(layer) ||
    isCircleShapeLayer(layer) ||
    isTriangleShapeLayer(layer)) &&
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  !!layer.strokeStyle &&
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  !!layer.shadowStyle &&
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  !!layer.shadowDirection &&
  !!layer.shadowDistance;

const layerTypeGuards = [
  isTextLayer,
  isImageLayer,
  isShapeLayer,
  isLineLayer,
  isAssetLayer,
] as const;

const hasRequiredLayerProperties = (
  fabricObject: FabricObject,
): fabricObject is FabricObject & {
  id: string;
  name: string;
  positionIndex: number;
} => {
  const layer = fabricObject as any;
  return (
    typeof layer.id === "string" &&
    typeof layer.name === "string" &&
    typeof layer.positionIndex === "number" &&
    typeof layer.layerType === "string"
  );
};

export const fabricObjectToLayer = (
  fabricObject: FabricObject,
): Layer | null => {
  if (!hasRequiredLayerProperties(fabricObject)) return null;

  const layer = fabricObject as Layer;

  for (const typeGuard of layerTypeGuards) {
    if (typeGuard(layer)) return layer;
  }

  return null;
};
